import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePowerDialer, useDialerAttempts } from '@/hooks/usePowerDialer';
import { PowerDialerConsole, PowerDialerDock, attemptName } from './PowerDialerConsole';

export function PowerDialerGlobalConsole() {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const dialer = usePowerDialer();

  // Rede de segurança: se a sessão for finalizada no servidor enquanto ainda
  // existe uma chamada ao vivo, mantemos o console na tela para o SDR não
  // perder os controles (mudo, teclado, transferir, desligar).
  const lastSessionRef = useRef<typeof dialer.session | null>(null);
  if (dialer.session) lastSessionRef.current = dialer.session;
  const fallbackSessionId = !dialer.session ? lastSessionRef.current?.id ?? null : null;
  const { data: fallbackAttempts = [] } = useDialerAttempts(fallbackSessionId);
  const fallbackConnected = fallbackAttempts.find((a) => a.status === 'connected') ?? null;

  useEffect(() => {
    if (dialer.session) setOpen(true);
  }, [dialer.session?.id]);

  useEffect(() => {
    if (!dialer.session && !fallbackConnected) lastSessionRef.current = null;
  }, [dialer.session, fallbackConnected]);

  const session = dialer.session ?? (fallbackConnected ? lastSessionRef.current : null);
  if (!session) return null;

  const attempts = dialer.session ? dialer.attempts : fallbackAttempts;
  const liveAttempts = dialer.session
    ? dialer.liveAttempts
    : fallbackConnected
      ? [fallbackConnected]
      : [];
  const connectedAttempt = dialer.session ? dialer.connectedAttempt : fallbackConnected;

  const handleEnd = async () => {
    const sessionId = session.id;
    await dialer.end();
    lastSessionRef.current = null;
    if (sessionId) navigate(`/outbound/discador/sessao/${sessionId}`);
  };

  return open ? (
    <PowerDialerConsole
      open
      onMinimize={() => setOpen(false)}
      session={session}
      attempts={attempts}
      liveAttempts={liveAttempts}
      connectedAttempt={connectedAttempt}
      isBusy={dialer.isBusy}
      onPause={dialer.pause}
      onResume={dialer.resume}
      onSkip={dialer.skip}
      onHangupConnected={dialer.hangupConnected}
      onHoldConnected={dialer.holdConnected}
      onUnholdConnected={dialer.unholdConnected}
      onTransferConnected={dialer.transferConnected}
      onStartTransfer={dialer.startTransfer}
      onCompleteTransfer={dialer.completeTransfer}
      onCancelTransfer={dialer.cancelTransfer}
      transferState={dialer.transferState}
      onSendDtmf={dialer.sendDtmf}
      onEnd={handleEnd}
      onToggleMute={dialer.toggleMute}
      isAudioReady={dialer.isDialerAudioReady}
    />
  ) : (
    <PowerDialerDock
      onOpen={() => setOpen(true)}
      liveCount={liveAttempts.length}
      connectedName={connectedAttempt ? attemptName(connectedAttempt) : null}
    />
  );
}
